import type { PropsWithChildren, RefObject } from 'react';
import { createContext, useContext, useState } from 'react';
import type { StoreApi } from 'zustand/vanilla';
import type { WithSelectors } from '../../../utils/state/zustand';
import { createSelectors } from '../../../utils/state/zustand';
import { usePageInstanceId } from '../../context/PageInstanceIdProvider';
import { createDataSourceRecordStore } from './createDataSourceRecordStore';
import {
  createDataSourceRecordStoreManager,
  type DataSourceRecordStoreManager,
} from './DataSourceRecordStoreManager';
import { createDataSourceRecordStoreSelectors } from './selectors';
import type {
  DataSourceRecordStoreRefType,
  DataSourceRecordStoreState,
  DataSourceRecordStoreType,
} from './types';
import { useDataSourceRecordStoreImperativeRef } from './useDataSourceRecordStoreImperativeRef';

type DataSourceRecordStoreWithSelectors = WithSelectors<StoreApi<DataSourceRecordStoreType>> & {
  subscribe: <T>(
    selector: (state: DataSourceRecordStoreType) => T,
    listener: (selectedState: T, previousSelectedState: T) => void,
    options?: { equalityFn?: (a: T, b: T) => boolean },
  ) => () => void;
};

// This is used for page level data source controller because it is hard to rename this everywhere we are keeping it as it is for now
// TODO: rename this to PageDataSourceRecordStoreContext
export const DataSourceRecordStoreManagerContext =
  createContext<DataSourceRecordStoreManager | null>(null);

const AppDataSourceRecordStoreContext = createContext<DataSourceRecordStoreWithSelectors | null>(
  null,
);

/**
 * Readiness precondition: when called inside a PageInstanceIdProvider, the
 * caller MUST be gated by a component that waits for depGraph.useInstanceIds()
 * to include the current instanceId (see GateWhenStoreReady in
 * InterfacePageInstance). Without the gate, manager.getStore(instanceId)
 * throws on first render because createPageInstance has not yet run.
 * Top-level callers (no instanceId) are always safe — the base store exists.
 */
export const useDataSourceRecordStore = () => {
  const instanceId = usePageInstanceId();
  const manager = useDataSourceRecordStoreManager();
  return manager.getStore(instanceId);
};

export const useDataSourceRecordStoreManager = () => {
  const manager = useContext(DataSourceRecordStoreManagerContext);
  if (!manager) {
    throw new Error('Missing DataSourceRecordStoreProvider');
  }
  return manager;
};

export const useAppDataSourceRecordStore = () => {
  const _useStore = useContext(AppDataSourceRecordStoreContext);
  if (!_useStore) {
    throw new Error('Missing AppDataSourceRecordStoreProvider');
  }
  return _useStore;
};

export function DataSourceRecordStoreProvider({
  initialValue,
  dataSourceRecordStoreRef,
  children,
}: PropsWithChildren<{
  initialValue: Partial<DataSourceRecordStoreState>;
  dataSourceRecordStoreRef?: RefObject<DataSourceRecordStoreRefType | undefined>;
}>) {
  const [manager] = useState(() => {
    const baseStore = createSelectors(createDataSourceRecordStore(initialValue));
    const createStore = (initial: Partial<DataSourceRecordStoreState>) =>
      createSelectors(createDataSourceRecordStore(initial));
    return createDataSourceRecordStoreManager(baseStore, createStore);
  });

  useDataSourceRecordStoreImperativeRef({
    store: manager.baseStore,
    dataSourceRecordStoreRef,
  });

  return (
    <DataSourceRecordStoreManagerContext.Provider value={manager}>
      {children}
    </DataSourceRecordStoreManagerContext.Provider>
  );
}

export function AppDataSourceRecordStoreProvider({
  initialValue,
  children,
}: PropsWithChildren<{
  initialValue: Partial<DataSourceRecordStoreState>;
}>) {
  const [store] = useState(() =>
    createDataSourceRecordStoreSelectors(createDataSourceRecordStore(initialValue)),
  );

  return (
    <AppDataSourceRecordStoreContext.Provider value={store}>
      {children}
    </AppDataSourceRecordStoreContext.Provider>
  );
}
